import React, { useContext, useEffect } from "react";

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import LocationOnRoundedIcon from '@mui/icons-material/LocationOnRounded';
import AuthContext from "../../context/auth/authContext";
import SpotReviewContext from "../../context/spot/spotReviewContext";
import SpotReviews from "../spots/SpotReviews";
import { useParams, useHistory, useLocation } from "react-router-dom";

const getBackLabel = (from) => {
  if (from === '/spots') return 'Back to Local Finds';
  if (from === '/my-journeys') return 'Back to My Journeys';
  if (from === '/destinations') return 'Back to Discover';
  if (from.startsWith('/destination/')) return 'Back to Trips';
  if (from.startsWith('/trip/')) return 'Back to Trip';
  return 'Back';
};

const SpotDetails = () => {
  const authContext = useContext(AuthContext);
  const { loadUser } = authContext;
  const { spot_id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const from = params.get('from') || '/spots';
  const backLabel = getBackLabel(from);

  const spotContext = useContext(SpotReviewContext);
  const { spots, getSpots } = spotContext;

  useEffect(() => {
    loadUser();
    // Load all spots so we can find this one by id. The Local Finds feed uses
    // proper pagination; this page just needs the single matching spot.
    getSpots(1, 10000);
    // eslint-disable-next-line
  }, []);

  const spot = spots ? spots.find((s) => String(s.id) === String(spot_id)) : null;

  let coverImage = "https://images.unsplash.com/photo-1542773998-9325f0a098d7?auto=format&fit=crop&w=800";
  if (spot && spot.top_review_photo && spot.top_review_photo.image_link) {
    coverImage = spot.top_review_photo.image_link;
  }

  return (
    <Box sx={{ width: '100%', pt: 8 }}>
      {/* Back navigation */}
      <Button
        startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
        onClick={() => history.push(from)}
        sx={{
          textTransform: 'none',
          color: 'text.secondary',
          fontWeight: 500,
          mb: 2,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {backLabel}
      </Button>

      {/* Spot header */}
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: { xs: 200, sm: 260 },
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
        }}
      >
        <Box
          component="img"
          src={coverImage}
          alt={spot?.name || 'Spot'}
          sx={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 60%)',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            p: { xs: 2, sm: 3 },
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: '#fff',
              fontWeight: 700,
              textShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            {spot?.name || 'Spot'}
          </Typography>
          {spot?.address && (
            <Typography
              component="a"
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.address)}`}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{
                color: 'rgba(255,255,255,0.85)',
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 0.5,
                textDecoration: 'none',
                cursor: 'pointer',
                '&:hover': { color: '#fff', textDecoration: 'underline' },
              }}
            >
              <LocationOnRoundedIcon sx={{ fontSize: 16 }} />
              {spot.address}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Reviews section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Reviews
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
          See what travelers are saying about this place.
        </Typography>
      </Box>
      <SpotReviews
        id={spot_id}
        fromPath={`/spot/${spot_id}?from=${encodeURIComponent(from)}`}
      />
    </Box>
  );
};

export default SpotDetails;
